import { ChevronRightRegular } from '@fluentui/react-icons';
import { orderBy } from 'lodash';
import moment from 'moment-timezone';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { LazyAvatarGroup } from '../../Avatar';
import { getCourseColor } from '../../Utils';
import {
  replaceClassNamePlaceholder,
  replaceGradeNamePlaceholder,
  UNKNOWN_CLASS_NAME,
  UNKNOWN_GRADE_NAME,
} from '../Classbook.utils';
import { MyCoursesColumns, MyCoursesEntry } from './MyCourses.interface';

interface MyCoursesListEntryProps {
  course: MyCoursesEntry;
  onClick: () => void;
  columns: MyCoursesColumns;
}

export const MyCoursesListEntry: React.FC<MyCoursesListEntryProps> = ({
  course,
  onClick,
  columns,
}) => {
  const { t } = useTranslation();

  // Memoize colors to prevent re-generation on each render
  const courseColor = useMemo(() => getCourseColor({ id: course.id }), [course.id]);

  const unknownClassPlacholder = useMemo(() => {
    return t ? replaceClassNamePlaceholder(UNKNOWN_CLASS_NAME, t) : UNKNOWN_CLASS_NAME;
  }, [t]);

  const unknownGradePlaceholder = useMemo(() => {
    return t ? replaceGradeNamePlaceholder(UNKNOWN_GRADE_NAME, t) : UNKNOWN_GRADE_NAME;
  }, [t]);

  const getGradeName = useCallback(
    (gradeName: string): string => (gradeName === unknownGradePlaceholder ? '' : gradeName),
    [unknownGradePlaceholder],
  );

  const getClassName = useCallback(
    (className: string): string => (className === unknownClassPlacholder ? '' : className),
    [unknownClassPlacholder],
  );

  const getClassCourseLabel = (gradeName: string, classNames: string[]): string => {
    const actualGradeName = getGradeName(gradeName);
    const actualClassNames = orderBy(classNames).map(getClassName);
    return actualGradeName && !actualClassNames.some((name) => name.length === 0)
      ? `${actualGradeName} / ${actualClassNames.join(' - ')}`
      : '';
  };

  const getNextOccurrence = (startDate: string, endDate: string) => {
    const start = moment(startDate);
    const end = moment(endDate);
    const nowTs = moment().unix();

    if (start.isSame(moment(), 'day')) {
      if (nowTs >= start.unix() && nowTs <= end.unix()) {
        return `${t('now', { defaultValue: 'Jetzt' })} (${start.format('LT')}-${end.format('LT')})`;
      }

      const time = start.format('LT');
      return `${start.fromNow()} (${time})`;
    }

    if (start.isBefore(moment().add('7', 'days'))) {
      return start.format('dddd, LT');
    }

    return start.format('dd, DD.MM., LT');
  };

  return (
    <li
      className="listEntry"
      style={{
        borderLeft: `5px solid ${courseColor}`,
      }}
    >
      <a
        onClick={onClick && onClick.bind(this)}
        role="button"
        aria-label={`${course.courseName}${
          getClassCourseLabel(course.gradeName, course.classNames)
            ? ', ' + getClassCourseLabel(course.gradeName, course.classNames)
            : ''
        }${
          course.nextOccurrenceStart && course.nextOccurenceEnd
            ? ', ' + getNextOccurrence(course.nextOccurrenceStart, course.nextOccurenceEnd)
            : ''
        }, ${t('classbook:mycourses:navigate_to_course_description', {
          defaultValue: 'Öffnet Unterseite des Kurses',
        })}`}
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && onClick) {
            onClick();
          }
        }}
      >
        <div
          className="hyperlink flex w-full gap-2"
          style={{
            fontWeight: 'bold',
          }}
        >
          <div className="col colCourseIcon">
            <LazyAvatarGroup
              shape="square"
              name={course.courseName}
              style={{ fontWeight: 'bold' }}
              groupId={course.id}
            />
            {course.quantityNotifications && course.quantityNotifications > 0 && (
              <div
                className="badge"
                aria-label={t('classbook:mycourses:notifications_badge', {
                  defaultValue: '{{count}} neue Benachrichtigungen',
                  count: course.quantityNotifications,
                })}
              >
                {course.quantityNotifications}
              </div>
            )}
          </div>

          {(columns & MyCoursesColumns.CourseName) === MyCoursesColumns.CourseName && (
            <div className="col colCourseName" aria-hidden="true">
              {course.courseName}
            </div>
          )}

          {(columns & MyCoursesColumns.ClassGrade) === MyCoursesColumns.ClassGrade && (
            <div className="col colClassGradeName" aria-hidden="true">
              {getClassCourseLabel(course.gradeName, course.classNames)}
            </div>
          )}

          {(columns & MyCoursesColumns.NextOccurrence) === MyCoursesColumns.NextOccurrence && (
            <div className="col colNextTeachingUnit" aria-hidden="true">
              {getNextOccurrence(course.nextOccurrenceStart, course.nextOccurenceEnd)}
            </div>
          )}
          <div className="action">
            <ChevronRightRegular aria-hidden="true" />
          </div>
        </div>
      </a>
    </li>
  );
};
